import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Separator } from './ui/separator';
import { Wallet, Zap, ArrowRight, CheckCircle2, ShieldCheck, Landmark, ArrowLeftRight, Banknote, ArrowDownToLine, Globe } from 'lucide-react';

const cryptoIcons = [Wallet, ArrowRight, Zap, CheckCircle2];
const cryptoColors = ['primary', 'primary-glow', 'neon-accent', 'success'];

const fiatIcons = [ShieldCheck, Landmark, ArrowLeftRight, Banknote];
const fiatColors = ['neon-accent', 'primary-glow', 'primary', 'success'];

const StepCard = ({ step, i, icons, colors }) => {
  const Icon = icons[i];
  const color = colors[i];
  return (
    <div className="relative">
      <Card data-testid={`how-step-${step.number}`}
        className="p-5 border-[hsl(var(--primary-glow)/0.35)] bg-[hsl(var(--surface)/0.9)] backdrop-blur-md hover-glow hover-lift transition-all duration-300 group relative overflow-hidden h-full shadow-[0_0_20px_hsl(var(--primary)/0.1)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary)/0.08)] to-[hsl(var(--neon-accent)/0.05)] group-hover:from-[hsl(var(--primary)/0.15)] group-hover:to-[hsl(var(--neon-accent)/0.1)] transition-all duration-500" />
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-3">
            <span className="text-5xl font-extrabold text-[hsl(var(--primary-glow))] text-neon">{step.number}</span>
            <div className={`p-2.5 rounded-xl bg-[hsl(var(--${color})/0.25)] shadow-[0_0_20px_hsl(var(--${color})/0.3)] group-hover:shadow-[0_0_30px_hsl(var(--${color})/0.5)] transition-all duration-300`}>
              <Icon className={`h-6 w-6 text-[hsl(var(--${color}))] drop-shadow-[0_0_8px_hsl(var(--${color}))]`} />
            </div>
          </div>
          <h3 className="text-lg font-bold mb-2 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.1)] group-hover:text-[hsl(var(--primary-glow))] group-hover:text-neon transition-all duration-300">{step.title}</h3>
          <Separator className="my-2 bg-[hsl(var(--primary-glow)/0.35)]" />
          <p className="text-sm text-white/90 leading-relaxed">{step.description}</p>
        </div>
      </Card>
      {i < 3 && (
        <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-30 items-center justify-center">
          <div className="bg-[hsl(var(--primary)/0.25)] rounded-full p-2 border border-[hsl(var(--primary-glow)/0.6)] shadow-[0_0_20px_hsl(var(--primary-glow)/0.5)] animate-glow-pulse">
            <ArrowRight className="h-4 w-4 text-white drop-shadow-[0_0_8px_hsl(var(--primary-glow))]" />
          </div>
        </div>
      )}
    </div>
  );
};

export const HowItWorks = () => {
  const { t } = useTranslation();

  const cryptoSteps = [
    { number: '01', title: t('howItWorks.step1_title'), description: t('howItWorks.step1_desc') },
    { number: '02', title: t('howItWorks.step2_title'), description: t('howItWorks.step2_desc') },
    { number: '03', title: t('howItWorks.step3_title'), description: t('howItWorks.step3_desc') },
    { number: '04', title: t('howItWorks.step4_title'), description: t('howItWorks.step4_desc') },
  ];

  const fiatSteps = [
    { number: '01', title: t('howItWorks.fiat_step1_title'), description: t('howItWorks.fiat_step1_desc') },
    { number: '02', title: t('howItWorks.fiat_step2_title'), description: t('howItWorks.fiat_step2_desc') },
    { number: '03', title: t('howItWorks.fiat_step3_title'), description: t('howItWorks.fiat_step3_desc') },
    { number: '04', title: t('howItWorks.fiat_step4_title'), description: t('howItWorks.fiat_step4_desc') },
  ];

  return (
    <section data-testid="how-section" className="pt-2 sm:pt-4 lg:pt-6 pb-14 sm:pb-20 lg:pb-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, hsl(258 26% 12%) 0%, hsl(262 30% 15%) 50%, hsl(258 26% 12%) 100%)' }}>
      <div className="absolute top-10 left-10 w-80 h-80 bg-[hsl(var(--primary))] opacity-[0.12] rounded-full blur-[100px] animate-float" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[hsl(var(--neon-accent))] opacity-[0.10] rounded-full blur-[100px] animate-float" style={{ animationDelay: '1s' }} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">
            <span className="text-gradient text-neon drop-shadow-[0_0_30px_hsl(var(--primary-glow)/0.6)]">{t('howItWorks.title')}</span>
          </h2>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">{t('howItWorks.subtitle')}</p>
        </div>

        {/* Flow 1: Receive Crypto */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.25)] flex items-center justify-center shadow-[0_0_15px_hsl(var(--primary)/0.3)]">
              <ArrowDownToLine className="w-5 h-5 text-[hsl(var(--primary-glow))] drop-shadow-[0_0_6px_hsl(var(--primary-glow))]" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">{t('howItWorks.crypto_flow_title')}</h3>
              <p className="text-white/65 text-sm">{t('howItWorks.crypto_flow_subtitle')}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative">
            {cryptoSteps.map((step, i) => (
              <StepCard key={i} step={step} i={i} icons={cryptoIcons} colors={cryptoColors} />
            ))}
          </div>
        </div>

        {/* Divider arrow between flows */}
        <div className="flex items-center justify-center mb-16">
          <div className="flex items-center gap-3">
            <div className="w-20 sm:w-32 h-px bg-gradient-to-r from-transparent to-[hsl(var(--primary-glow)/0.5)]" />
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(var(--primary)/0.3)] to-[hsl(var(--neon-accent)/0.3)] border border-[hsl(var(--primary-glow)/0.5)] flex items-center justify-center shadow-[0_0_30px_hsl(var(--primary-glow)/0.3)] animate-glow-pulse">
              <ArrowRight className="w-5 h-5 text-white rotate-90 drop-shadow-[0_0_8px_hsl(var(--primary-glow))]" />
            </div>
            <div className="w-20 sm:w-32 h-px bg-gradient-to-l from-transparent to-[hsl(var(--neon-accent)/0.5)]" />
          </div>
        </div>

        {/* Flow 2: Settle to Fiat */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--neon-accent)/0.25)] flex items-center justify-center shadow-[0_0_15px_hsl(var(--neon-accent)/0.3)]">
              <Globe className="w-5 h-5 text-[hsl(var(--neon-accent))] drop-shadow-[0_0_6px_hsl(var(--neon-accent))]" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">{t('howItWorks.fiat_flow_title')}</h3>
              <p className="text-white/65 text-sm">{t('howItWorks.fiat_flow_subtitle')}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative">
            {fiatSteps.map((step, i) => (
              <StepCard key={i} step={step} i={i} icons={fiatIcons} colors={fiatColors} />
            ))}
          </div>
        </div>

        <div className="text-center">
          <p className="text-white/70 text-sm mb-4">{t('howItWorks.cta_ready')}</p>
          <a href="#integration" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[hsl(var(--primary))] text-white font-medium shadow-[0_0_25px_hsl(var(--primary)/0.4)] hover:shadow-[0_0_40px_hsl(var(--primary)/0.6)] transition-all duration-300 hover:scale-105">
            {t('howItWorks.cta_docs')}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
